package er.prateek.crossly.persistence

import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.game.crossword.KeyboardMode
import er.prateek.crossly.game.crossword.StarterAmount

/** Who put a value in place -- decides whether a new Remote Config default may replace it. */
enum class ChoiceSource {
    /** The player picked it in Settings. Config never overrides this. */
    USER,

    /** It came from Remote Config's `default` and follows that default when it changes. */
    CONFIG,
}

data class StoredChoice<T>(val value: T, val source: ChoiceSource)

/**
 * How the player has chosen to be helped while solving.
 *
 * Letter keys and starter letters are kept per [DifficultyTier] -- a player can want the full
 * keyboard on Expert and a trimmed rack on Easy. A tier missing from the maps has never been
 * stored on this device and takes Remote Config's default for that tier.
 */
data class GameplayPreferences(
    val keyboardModes: Map<DifficultyTier, StoredChoice<KeyboardMode>> = emptyMap(),
    val starterAmounts: Map<DifficultyTier, StoredChoice<StarterAmount>> = emptyMap(),
    val showClueGutter: Boolean = true,
)

interface GameplayPrefsStore {
    fun load(): GameplayPreferences
    fun save(preferences: GameplayPreferences)
}

/**
 * The fresh-install values builds before per-level preferences shipped would have saved. A
 * value still equal to one of these may never have been a deliberate pick -- every gameplay
 * setting was written whenever *any* of them changed -- so it's migrated as [ChoiceSource.CONFIG].
 */
internal val LEGACY_DEFAULT_KEYBOARD_MODE = KeyboardMode.THIS_PUZZLE
internal val LEGACY_DEFAULT_STARTER_AMOUNT = StarterAmount.FEW

/**
 * How a pre-per-level global value carries into each tier: anything but the old default must
 * have been picked by the player, so it stays theirs; the old default follows config from now on.
 */
internal fun <T> legacyChoice(value: T, legacyDefault: T): StoredChoice<T> =
    StoredChoice(value, if (value == legacyDefault) ChoiceSource.CONFIG else ChoiceSource.USER)
